/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout, { 
  Card, 
  CardHeader, 
  ActionButton, 
  ErrorState,
  StatusBadge 
} from '../../../components/admin/InventoryLayout';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmVariant = "danger", isLoading = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="max-w-md w-full mx-4">
        <CardHeader
          title={title}
          icon={
            <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          }
        />
        
        <div className="p-6">
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">
            {message}
          </p>
          
          <div className="flex gap-3">
            <ActionButton
              onClick={onClose}
              variant="secondary"
              size="md"
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={onConfirm}
              variant={confirmVariant}
              size="md"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Processing...' : confirmText}
            </ActionButton>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Quick Supplier Modal Component
const QuickSupplierModal = ({ isOpen, onClose, onSave }) => {
  const [supplierData, setSupplierData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplierData.name.trim()) return;
    
    setSaving(true);
    try {
      await onSave(supplierData);
      setSupplierData({ name: '', contactPerson: '', email: '', phone: '', address: '' });
    } catch (error) {
      console.error('Error creating supplier:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="max-w-lg w-full mx-4">
        <CardHeader
          title="Add New Supplier"
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              required
              value={supplierData.name}
              onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="Enter company name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Contact Person
            </label>
            <input
              type="text"
              value={supplierData.contactPerson}
              onChange={(e) => setSupplierData({ ...supplierData, contactPerson: e.target.value })}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="Enter contact person"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={supplierData.email}
                onChange={(e) => setSupplierData({ ...supplierData, email: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={supplierData.phone}
                onChange={(e) => setSupplierData({ ...supplierData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter phone"
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ActionButton
              onClick={onClose}
              variant="secondary"
              size="md"
              className="flex-1"
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleSubmit}
              variant="accent"
              size="md"
              disabled={saving || !supplierData.name.trim()}
              className="flex-1 !bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
            >
              {saving ? 'Creating...' : 'Add Supplier'}
            </ActionButton>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Image Upload Component (same as AddProduct)
const ImageUploadCard = ({ value, onChange }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (file) => {
    if (!file) return;

    setError('');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    
    // Create a data URL for preview (in a real app, you'd upload to server)
    const reader = new FileReader();
    reader.onload = (e) => {
      onChange(e.target.result);
      setUploading(false);
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  return (
    <div className="space-y-3">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          error
            ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-900/20'
            : dragOver 
            ? 'border-[#7d6633] bg-[#7d6633]/5' 
            : 'border-white/20 dark:border-white/10 hover:border-[#7d6633]/50'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7d6633] mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400">Uploading image...</p>
          </div>
        ) : value ? (
          <div className="flex flex-col items-center">
            <img 
              src={value} 
              alt="Product preview" 
              className="w-32 h-32 object-cover rounded-lg mb-4 border border-white/20"
            />
            <p className="text-zinc-600 dark:text-zinc-400 mb-2">Click to change image</p>
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setError('');
              }}
              variant="danger"
              size="xs"
            >
              Remove Image
            </ActionButton>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <svg className="h-12 w-12 text-zinc-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-2">
              Drag and drop your product image here
            </p>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-4">
              or click to browse files
            </p>
            <div className="text-xs text-zinc-400">
              Supports: JPG, PNG, GIF, WebP (Max 5MB)
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

const EditProduct = () => {
  const { user, hasPermission } = useAdminAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [product, setProduct] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: '',
    unit: '',
    sku: '',
    barcode: '',
    defaultSupplierId: '',
    imageUrl: '',
    reorderLeadTimeDays: 7,
    description: '',
    active: true
  });

  useEffect(() => {
    if (id) {
      loadProductData();
    }
  }, [id]);

  const loadProductData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all data in parallel
      const [productRes, suppliersRes, categoriesRes, unitsRes, locationsRes] = await Promise.all([
        inventoryApi.items.getItem(id),
        inventoryApi.suppliers.getSuppliers().catch(() => ({ suppliers: [] })),
        inventoryApi.categories.getCategories().catch(() => ({ categories: [] })),
        inventoryApi.units.getUnits().catch(() => ({ units: [] })),
        inventoryApi.locations.getLocations().catch(() => ({ locations: [] }))
      ]);

      // Set form options data
      setSuppliers(suppliersRes.suppliers || []);
      setCategories(categoriesRes.categories || ['Shampoo', 'Conditioner', 'Styling', 'Tools', 'Supplies']);
      setUnits(unitsRes.units || ['piece', 'bottle', 'tube', 'jar', 'pack', 'liter', 'ml', 'gram', 'kg']);
      setLocations(locationsRes.locations || []);
      
      // Set product data
      const productData = productRes.item || productRes;
      
      if (!productData || !productData.id) {
        throw new Error('Product not found');
      }

      setProduct(productData);
      setFormData({
        name: productData.name || '',
        brand: productData.brand || '',
        category: productData.category || '',
        unit: productData.unit || '',
        sku: productData.sku || '',
        barcode: productData.barcode || '',
        defaultSupplierId: productData.defaultSupplier?.id || productData.defaultSupplierId || '',
        imageUrl: productData.imageUrl || '',
        reorderLeadTimeDays: productData.reorderLeadTimeDays || 7,
        description: productData.description || '',
        active: productData.active !== false,
        status: productData.status || 'draft'
      });

    } catch (err) {
      console.error('Error loading product data:', err);
      setError('Failed to load product data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSKU = () => {
    const prefix = formData.category ? formData.category.substring(0, 3).toUpperCase() : 'PRD';
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${timestamp}-${randomNum}`;
  };

  const handleGenerateSKU = () => {
    const newSKU = generateSKU();
    handleInputChange('sku', newSKU);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Product name is required');
      return false;
    }
    if (!formData.category) {
      setError('Please select a category');
      return false;
    }
    if (!formData.unit) {
      setError('Please select a unit');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const updateData = {
        ...formData,
        reorderLeadTimeDays: parseInt(formData.reorderLeadTimeDays) || 7
      };

      await inventoryApi.items.updateItem(id, updateData);
      
      // Navigate back to products page with success message
      navigate('/admin/inventory/products', { 
        state: { 
          message: `Product "${formData.name}" updated successfully!`,
          type: 'success'
        }
      });
      
    } catch (err) {
      console.error('Error updating product:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update product. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveProduct = () => {
    setShowArchiveConfirm(true);
  };

  const confirmArchiveProduct = async () => {
    try {
      setSaving(true);
      setError('');

      if (formData.active) {
        await inventoryApi.items.archiveItem(id);
      } else {
        // Reactivate - update with active: true
        await inventoryApi.items.updateItem(id, { ...formData, active: true });
      }
      
      // Reload product data
      await loadProductData();
      setShowArchiveConfirm(false);
      
    } catch (err) {
      console.error('Error updating product status:', err);
      setError(`Failed to ${formData.active ? 'archive' : 'activate'} product`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/inventory/products');
  };

  const handleViewDetails = () => {
    navigate(`/admin/inventory/products/${id}`);
  };

  const handleCreateSupplier = async (supplierData) => {
    try {
      const response = await inventoryApi.suppliers.createSupplier(supplierData);
      
      // Reload suppliers list
      const updatedSuppliers = await inventoryApi.suppliers.getSuppliers();
      setSuppliers(updatedSuppliers.suppliers || []);
      
      // Auto-select the new supplier
      if (response.supplier?.id) {
        handleInputChange('defaultSupplierId', response.supplier.id);
      }
      
      setShowSupplierModal(false);
    } catch (err) {
      console.error('Error creating supplier:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <InventoryLayout 
        title="Edit Product" 
        subtitle="Update product information"
        currentTab="products"
      >
        <Card>
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7d6633] mx-auto mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400">Loading product data...</p>
      </div>
        </Card>
      </InventoryLayout>
    );
  }

  if (error && !product) {
    return (
      <InventoryLayout 
        title="Edit Product" 
        subtitle="Update product information"
        currentTab="products"
      >
        <ErrorState error={error} onRetry={loadProductData} />
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout 
      title={`Edit: ${product?.name || 'Product'}`}
      subtitle="Update product information and settings"
      currentTab="products"
      action={
        <div className="flex items-center gap-3">
          <StatusBadge 
            status={formData.active ? 'Active' : 'Archived'} 
            variant={formData.active ? 'success' : 'default'} 
          />
          <ActionButton
            onClick={handleViewDetails}
            variant="secondary"
            size="md"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Details
          </ActionButton>
          <ActionButton
                onClick={handleCancel}
            variant="secondary"
            size="md"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
                Cancel
          </ActionButton>
          <ActionButton
                onClick={handleSubmit}
            variant="accent"
            size="md"
                disabled={saving}
            className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {saving ? 'Saving Changes...' : 'Save Changes'}
          </ActionButton>
        </div>
      }
    >
      {error && <ErrorState error={error} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Product Status & Actions */}
        <Card>
          <CardHeader
            title="Product Status"
            subtitle="Manage product status and quick actions"
            icon={
              <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <StatusBadge 
                  status={formData.active ? 'Active' : 'Archived'} 
                  variant={formData.active ? 'success' : 'default'} 
                />
                {product?.totalStock !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">Current Stock:</span>
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{product.totalStock}</span>
                  </div>
                )}
      </div>

              <div className="flex gap-2">
                <ActionButton
                  onClick={handleArchiveProduct}
                  variant={formData.active ? 'warning' : 'success'}
                  size="sm"
                  disabled={saving}
                >
                  {formData.active ? 'Archive Product' : 'Activate Product'}
                </ActionButton>
              </div>
            </div>
          </div>
        </Card>

        {/* Basic Product Information */}
        <Card>
          <CardHeader
            title="Basic Information"
            subtitle="Essential product details"
            icon={
              <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          
          <div className="p-4 md:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
                <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                  required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50 text-lg"
                    placeholder="Enter product name"
                  />
                </div>

                {/* Brand */}
                <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    Brand
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                    placeholder="Enter brand name"
                  />
                </div>

                {/* Category */}
                <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Category *
                  </label>
                  <select
                  required
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Unit */}
                <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Unit of Measurement *
                  </label>
                  <select
                  required
                    value={formData.unit}
                    onChange={(e) => handleInputChange('unit', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                >
                    {units.map(unit => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Default Supplier */}
                <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Default Supplier
                  </label>
                  <ActionButton
                    onClick={() => setShowSupplierModal(true)}
                    variant="accent"
                    size="xs"
                    className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
                  >
                    <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New
                  </ActionButton>
                </div>
                  <select
                    value={formData.defaultSupplierId}
                    onChange={(e) => handleInputChange('defaultSupplierId', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                >
                  <option value="">Select supplier (optional)</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
              </div>
                </div>

            {/* Description */}
                <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Description
                  </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Describe the product, its uses, and key features..."
                  />
                </div>
              </div>
        </Card>

            {/* Product Identification */}
        <Card>
          <CardHeader
            title="Product Identification"
            subtitle="SKU, barcode, and tracking information"
            icon={
              <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
          
          <div className="p-4 md:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SKU */}
                <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    SKU (Stock Keeping Unit)
                  </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => handleInputChange('sku', e.target.value)}
                    className="flex-1 px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                    placeholder="Enter SKU"
                  />
                  <ActionButton
                    onClick={handleGenerateSKU}
                    variant="accent"
                    size="md"
                    className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429] px-4"
                  >
                    Generate
                  </ActionButton>
                </div>
                </div>

                {/* Barcode */}
                <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                    Barcode
                  </label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => handleInputChange('barcode', e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                    placeholder="Enter barcode"
                  />
                </div>

              {/* Reorder Lead Time */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Reorder Lead Time (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.reorderLeadTimeDays}
                  onChange={(e) => handleInputChange('reorderLeadTimeDays', parseInt(e.target.value) || 7)}
                  className="w-full md:w-48 px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                />
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                  How many days it typically takes to restock this item
                </p>
              </div>

              {/* Product Status */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Product Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full md:w-64 px-4 py-3 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                >
                  <option value="draft">Draft - Product being created, not available yet</option>
                  <option value="active">Active - Product is available and ready for use</option>
                  <option value="archived">Archived - Product is no longer available</option>
                </select>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                  {formData.status === 'draft' && 'Product is in draft mode - change to Active when ready'}
                  {formData.status === 'active' && 'Product is currently active and available for operations'}
                  {formData.status === 'archived' && 'Product is archived and hidden from operations'}
                </p>
              </div>
            </div>
          </div>
        </Card>

            {/* Product Image */}
        <Card>
          <CardHeader
            title="Product Image"
            subtitle="Upload or update product image"
            icon={
              <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
          
          <div className="p-4 md:p-6">
            <ImageUploadCard
                value={formData.imageUrl}
                onChange={(value) => handleInputChange('imageUrl', value)}
              />
            </div>
        </Card>

            {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-6">
          <ActionButton
                onClick={handleCancel}
            variant="secondary"
            size="lg"
              >
                Cancel
          </ActionButton>
          <ActionButton
            onClick={handleSubmit}
            variant="accent"
            size="lg"
                disabled={saving}
            className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
              >
                {saving ? 'Saving Changes...' : 'Save Changes'}
          </ActionButton>
        </div>
      </form>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={confirmArchiveProduct}
        title={`${formData.active ? 'Archive' : 'Activate'} Product`}
        message={`Are you sure you want to ${formData.active ? 'archive' : 'activate'} this product? ${
          formData.active 
            ? 'This will remove it from active inventory listings.' 
            : 'This will restore it to active inventory listings.'
        }`}
        confirmText={formData.active ? 'Archive Product' : 'Activate Product'}
        confirmVariant={formData.active ? 'danger' : 'success'}
        isLoading={saving}
      />

      {/* Quick Supplier Modal */}
      <QuickSupplierModal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSave={handleCreateSupplier}
      />
    </InventoryLayout>
  );
};

export default EditProduct;